import { ANALYTICS_ENABLED, ANALYTICS_HOSTNAMES, GA_MEASUREMENT_ID } from "../config";
import { initMetaPixel, trackMetaPixelEvent, trackMetaPixelPageView } from "./metaPixel";

let gaInitialized = false;

function canUseBrowser() {
  return typeof window !== "undefined" && typeof document !== "undefined";
}

function canUseGa() {
  return Boolean(GA_MEASUREMENT_ID) && canTrackOnCurrentHost();
}

export function canTrackOnCurrentHost() {
  if (!ANALYTICS_ENABLED || !canUseBrowser()) return false;
  return ANALYTICS_HOSTNAMES.includes(window.location.hostname.toLowerCase());
}

function normalizeParams(params = {}) {
  return Object.fromEntries(
    Object.entries(params).filter(([, value]) => value !== undefined && value !== null && value !== "")
  );
}

function productParams(product = {}) {
  const id = String(product._id || product.id || product.slug || product.name || "");

  return normalizeParams({
    item_id: id,
    item_name: product.name,
    item_category: product.type,
    price: Number(product.price) || undefined,
  });
}

export function initAnalytics() {
  initMetaPixel();

  if (!canUseGa() || gaInitialized) return;

  window.dataLayer = window.dataLayer || [];
  window.gtag = function gtag() {
    window.dataLayer.push(arguments);
  };
  window.gtag("js", new Date());
  window.gtag("config", GA_MEASUREMENT_ID, { send_page_view: false });

  const script = document.createElement("script");
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(GA_MEASUREMENT_ID)}`;
  document.head.appendChild(script);

  gaInitialized = true;
}

export function trackAnalyticsEvent(eventName, params = {}, metaEventName) {
  const cleanParams = normalizeParams(params);

  initAnalytics();

  if (canUseGa() && typeof window.gtag === "function") {
    window.gtag("event", eventName, cleanParams);
  }

  if (metaEventName) {
    trackMetaPixelEvent(metaEventName, cleanParams);
  }
}

export function trackPageView(pathname, search = "") {
  if (!canUseBrowser()) return;

  initAnalytics();
  trackMetaPixelPageView();

  if (canUseGa() && typeof window.gtag === "function") {
    window.gtag("event", "page_view", {
      page_title: document.title,
      page_location: window.location.href,
      page_path: `${pathname}${search}`,
    });
  }
}

export function trackProductView(product, extraParams = {}) {
  const params = {
    ...productParams(product),
    ...extraParams,
  };

  trackAnalyticsEvent(
    "view_item",
    {
      ...params,
      items: [params],
    },
    "ViewContent"
  );
}

export function trackProductSelect(product, extraParams = {}) {
  const params = {
    ...productParams(product),
    ...extraParams,
  };

  trackAnalyticsEvent("select_item", {
    ...params,
    items: [params],
  });
}

export function trackContactClick(channel, params = {}) {
  const normalizedChannel = String(channel || "").toLowerCase();
  let eventName = "lead_event";

  if (normalizedChannel.includes("zalo")) eventName = "click_zalo";
  else if (normalizedChannel.includes("phone") || normalizedChannel.includes("hotline") || normalizedChannel.includes("gọi")) {
    eventName = "click_hotline";
  } else if (normalizedChannel.includes("facebook") || normalizedChannel.includes("messenger") || normalizedChannel.includes("fanpage")) {
    eventName = "click_facebook";
  }

  trackAnalyticsEvent(eventName, {
    contact_channel: channel,
    ...params,
  });

  trackAnalyticsEvent(
    "generate_lead",
    {
      contact_channel: channel,
      ...params,
    },
    "Contact"
  );
}

export function trackQuoteFormSubmit(params = {}) {
  trackAnalyticsEvent("submit_quote_form", params);
  trackAnalyticsEvent("generate_lead", { lead_source: "quote_form", ...params }, "Lead");
}

export function trackPurchase(params = {}) {
  trackAnalyticsEvent("purchase", params, "Purchase");
}
